import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import { refreshToken } from '@routes/auth/___shared___/auth.slice.ts';
import {
    deleteRefreshToken,
    deleteRefreshTokenExpiresAtUTC,
    deleteToken,
    readRefreshToken,
    readRefreshTokenExpiresAtUTC,
} from '@store/services/localStorage.service.ts';
import { redirectToLoginWithCallbackUrl } from '@utils/navigation.ts';
import { jwtDecode } from 'jwt-decode';

type JwtPayload = { exp?: number; [k: string]: unknown };

let refreshTimer: number | null = null;
let refreshInFlight: Promise<void> | null = null;

const REFRESH_BEFORE_EXPIRY_MS = 30 * 1000;

export const scheduleTokenRefresh = (
    token: string | null,
    refreshTokenExpiresAtUTC: string | null,
    dispatch: AppDispatchFn,
): void => {
    clearTokenExpirationTimer();

    if (!token || !refreshTokenExpiresAtUTC) return;

    const delayMs = getRefreshDelayMs(token, refreshTokenExpiresAtUTC);

    if (delayMs === null) return;

    if (delayMs <= 0) {
        void handleTokenRefresh(dispatch);
        return;
    }

    refreshTimer = window.setTimeout(() => {
        void handleTokenRefresh(dispatch);
    }, delayMs);
};

const getRefreshDelayMs = (accessToken: string, refreshTokenExpiresAtUTC: string): number | null => {
    const accessTokenExp = decodeTokenExpirationTime(accessToken);

    if (!accessTokenExp) return null;

    const nowMs = Date.now();
    const accessTokenExpiresAtMs = accessTokenExp * 1000;
    const refreshTokenExpiresAtMs = new Date(refreshTokenExpiresAtUTC).getTime();

    if (!Number.isFinite(refreshTokenExpiresAtMs)) return null;

    const earliestExpirationMs = Math.min(accessTokenExpiresAtMs, refreshTokenExpiresAtMs);

    const delayMs = earliestExpirationMs - nowMs - REFRESH_BEFORE_EXPIRY_MS;

    return Math.max(delayMs, 0);
};

const decodeTokenExpirationTime = (token: string | null): number | null => {
    if (!token) return null;

    try {
        const { exp } = jwtDecode<JwtPayload>(token);

        return typeof exp === 'number' ? exp : null;
    } catch {
        return null;
    }
};

export const clearTokenExpirationTimer = (): void => {
    if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
        refreshTimer = null;
    }
};

// refresh logic - one refresh request at a time
// if no refresh token available - clean and stop
// if refresh successful - save new tokens and schedule next refresh based on token expiration time
// if refresh failed - clear tokens and timers
const handleTokenRefresh = async (dispatch: AppDispatchFn): Promise<void> => {
    if (refreshInFlight) return refreshInFlight;

    const refreshTokenValue = readRefreshToken();

    if (!refreshTokenValue) {
        deleteToken();
        deleteRefreshToken();
        deleteRefreshTokenExpiresAtUTC();
        clearTokenExpirationTimer();
        return;
    }

    const doRefresh = async () => {
        try {
            const res = await dispatch(refreshToken({ refreshToken: refreshTokenValue }));

            scheduleTokenRefresh(res.accessToken, res.refreshTokenExpiresAtUTC, dispatch);
        } catch (e) {
            console.error('Refresh failed', e);
            deleteToken();
            deleteRefreshToken();
            deleteRefreshTokenExpiresAtUTC();
            clearTokenExpirationTimer();
            redirectToLoginWithCallbackUrl();
        } finally {
            refreshInFlight = null;
        }
    };

    refreshInFlight = doRefresh();

    return refreshInFlight;
};

// set timer to refresh token before it expires, if no token - clear timer and stop
export const initTokenRefreshScheduler = (token: string | null, dispatch: AppDispatchFn): void => {
    if (!token) {
        clearTokenExpirationTimer();
        return;
    }

    scheduleTokenRefresh(token, readRefreshTokenExpiresAtUTC(), dispatch);
};

export const onTokensUpdated = (
    newAccessToken: string,
    refreshTokenExpiresAtUTC: string,
    dispatch: AppDispatchFn,
): void => {
    scheduleTokenRefresh(newAccessToken, refreshTokenExpiresAtUTC, dispatch);
};

export const cancelTokenRefreshTimer = (): void => {
    clearTokenExpirationTimer();
};

// Fallback validation for cases where the scheduled refresh did not run in time.
export const validateTokenAndRefreshIfNecessary = async (
    token: string | null,
    dispatch: AppDispatchFn,
): Promise<void> => {
    if (!token) return;

    const refreshTokenExpiresAtUTC = readRefreshTokenExpiresAtUTC();

    if (!refreshTokenExpiresAtUTC) {
        cancelTokenRefreshTimer();
        return;
    }

    const accessTokenExp = decodeTokenExpirationTime(token);

    if (!accessTokenExp) {
        cancelTokenRefreshTimer();
        return;
    }

    const nowMs = Date.now();
    const accessTokenExpiresAtMs = accessTokenExp * 1000;
    const refreshTokenExpiresAtMs = new Date(refreshTokenExpiresAtUTC).getTime();

    if (nowMs >= accessTokenExpiresAtMs || nowMs >= refreshTokenExpiresAtMs) {
        await handleTokenRefresh(dispatch);
    }
};
